import {Platform} from 'react-native';
import Config from 'react-native-config';

const SPLUNK_HEC_URL =
  'https://http-inputs-medifast.splunkcloud.com/services/collector/event';

const isWebProxyEnabled =
  Platform.OS === 'web' && Config?.SPLUNK_PROXY_ENABLED === 'false';

const getSplunkEndpoint = () => {
  if (isWebProxyEnabled && Config?.SPLUNK_WEB_PROXY_URL) {
    return Config.SPLUNK_WEB_PROXY_URL;
  }

  return SPLUNK_HEC_URL;
};

const getSplunkHeaders = (authorizationKey: string) => {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };

  // For direct-to-Splunk HEC calls we need Authorization.
  // For web proxy endpoints, avoid forwarding this secret from the browser.
  if (
    !(isWebProxyEnabled && Config?.SPLUNK_WEB_PROXY_URL) &&
    authorizationKey
  ) {
    headers.Authorization = authorizationKey;
  }

  return headers;
};

const getEpochTime = (timestamp?: string) => {
  if (!timestamp) {
    return Date.now();
  }

  const parsed = Date.parse(timestamp);
  return Number.isNaN(parsed) ? Date.now() : parsed;
};

const getResponseReceivedTime = (responseData: any) =>
  responseData?.responseReceivedTime || responseData?.responseReceiveTime;

const getExecutionTotalTime = (
  requestTimestamp?: string,
  responseData?: any,
) => {
  if (!requestTimestamp) {
    return 0;
  }

  const requestMs = Date.parse(requestTimestamp);
  const responseMs = Date.parse(getResponseReceivedTime(responseData));

  if (Number.isNaN(requestMs) || Number.isNaN(responseMs)) {
    return 0;
  }

  return responseMs - requestMs;
};

/**
 * Common splunk log for requests
 */
export const requestLog = ({
  authorizationKey,
  deviceInfo,
  logType,
  message,
  requestData,
  requestId,
  responseData,
  severity,
  source,
  sourceType,
}: any) => {
  const endpoint = getSplunkEndpoint();

  return fetch(endpoint, {
    method: 'POST',
    headers: getSplunkHeaders(authorizationKey),
    body: JSON.stringify({
      time: getEpochTime(requestData?.timestamp),
      source: source,
      sourcetype: sourceType,
      event: {
        message: message,
        logType: logType,
        severity: severity,
        platform: {
          version: deviceInfo.version,
          deviceId: deviceInfo.uniqueId,
          OS: deviceInfo.OS,
          hostOS: deviceInfo.hostOS,
          hostOSVersion: deviceInfo.hostOSVersion,
          browser: deviceInfo.browser,
          browserVersion: deviceInfo.browserVersion,
        },
        requestId: requestId,
        requestQueryParameters: requestData?.requestQueryParameters,
        requestHeaders: requestData?.requestHeaders,
        requestBody: requestData?.requestBody,
        responseHeaders: responseData?.responseHeaders,
        responseBody: responseData?.responseBody,
        statusCode: responseData?.statusCode,
        executionTime: {
          requestSentTime: requestData?.timestamp,
          responseReceivedTime: getResponseReceivedTime(responseData),
          totalTime: getExecutionTotalTime(
            requestData?.timestamp,
            responseData,
          ),
        },
      },
    }),
  });
};

/**
 * Manual splunk log for specific cases
 */
export const manualLog = ({
  authorizationKey,
  deviceInfo,
  extraData,
  loginType,
  message,
  severity,
  source,
  sourceType,
}: any) => {
  const endpoint = getSplunkEndpoint();

  return fetch(endpoint, {
    method: 'POST',
    headers: getSplunkHeaders(authorizationKey),
    body: JSON.stringify({
      time: new Date().getTime(),
      source: source,
      sourcetype: sourceType,
      event: {
        logType: 'manual',
        message: message,
        severity: severity,
        platform: {
          version: deviceInfo.version,
          deviceId: deviceInfo.uniqueId,
          OS: deviceInfo.OS,
          hostOS: deviceInfo.hostOS,
          hostOSVersion: deviceInfo.hostOSVersion,
          browser: deviceInfo.browser,
          browserVersion: deviceInfo.browserVersion,
        },
        loginType: loginType,
        extraData: extraData,
      },
    }),
  });
};
